import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Visibility as VisibilityIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { getFirestore, collection, query, orderBy, getDocs, doc, updateDoc, deleteDoc, where } from 'firebase/firestore';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { AuthContext } from '../contexts/AuthContext';
import AccessDenied from '../components/Common/AccessDenied';

const statusColors = {
  new: 'info',
  inProgress: 'warning',
  resolved: 'success',
  rejected: 'error'
};

const statusLabels = {
  new: 'Nowy',
  inProgress: 'W trakcie',
  resolved: 'Rozwiązany',
  rejected: 'Odrzucony'
};

const ErrorReports = () => {
  const { userRole } = useContext(AuthContext);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (userRole === 'admin') {
      fetchReports();
    }
  }, [statusFilter, userRole]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      let reportsQuery;

      if (statusFilter === 'all') {
        reportsQuery = query(collection(db, 'bugReports'), orderBy('timestamp', 'desc'));
      } else {
        reportsQuery = query(
          collection(db, 'bugReports'), 
          where('status', '==', statusFilter),
          orderBy('timestamp', 'desc')
        );
      }

      const querySnapshot = await getDocs(reportsQuery);
      const reportsList = [];
      
      querySnapshot.forEach((doc) => {
        reportsList.push({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp ? new Date(doc.data().timestamp) : new Date()
        });
      });
      
      setReports(reportsList);
      setLoading(false);
    } catch (error) {
      console.error('Błąd podczas pobierania zgłoszeń:', error);
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (report) => {
    setSelectedReport(report);
    setComment(report.adminComment || '');
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedReport(null);
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedReport) return;

    try {
      const db = getFirestore();
      const reportRef = doc(db, 'bugReports', selectedReport.id);
      
      await updateDoc(reportRef, {
        status: newStatus,
        adminComment: comment,
        updatedAt: new Date().toISOString()
      });

      // Aktualizacja lokalnego stanu
      setReports(reports.map(report => 
        report.id === selectedReport.id 
          ? { ...report, status: newStatus, adminComment: comment, updatedAt: new Date() } 
          : report
      ));
      
      setDetailsOpen(false);
      fetchReports(); // Odświeżenie listy
    } catch (error) {
      console.error('Błąd podczas aktualizacji statusu:', error);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (window.confirm('Czy na pewno chcesz usunąć to zgłoszenie? Ta operacja jest nieodwracalna.')) {
      try {
        const db = getFirestore();
        await deleteDoc(doc(db, 'bugReports', reportId));
        
        // Aktualizacja lokalnego stanu
        setReports(reports.filter(report => report.id !== reportId));
        
        if (detailsOpen && selectedReport?.id === reportId) {
          setDetailsOpen(false);
        }
      } catch (error) {
        console.error('Błąd podczas usuwania zgłoszenia:', error);
      }
    }
  };

  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const handleFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const formatDate = (date) => {
    if (!date) return 'Brak daty';
    return format(date, 'dd.MM.yyyy HH:mm', { locale: pl });
  };

  // Sprawdzenie czy użytkownik jest administratorem
  if (userRole !== 'admin') {
    return <AccessDenied />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Zgłoszenia błędów
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <FormControl variant="outlined" sx={{ minWidth: 200 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            id="status-filter"
            value={statusFilter}
            onChange={handleFilterChange}
            label="Status"
          >
            <MenuItem value="all">Wszystkie</MenuItem>
            <MenuItem value="new">Nowe</MenuItem>
            <MenuItem value="inProgress">W trakcie</MenuItem>
            <MenuItem value="resolved">Rozwiązane</MenuItem>
            <MenuItem value="rejected">Odrzucone</MenuItem>
          </Select>
        </FormControl>

        <Button 
          variant="outlined" 
          onClick={fetchReports}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          disabled={loading}
        >
          Odśwież
        </Button>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                <TableCell>Data zgłoszenia</TableCell>
                <TableCell>Użytkownik</TableCell>
                <TableCell>Strona</TableCell>
                <TableCell>Opis</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Akcje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Brak zgłoszeń
                  </TableCell>
                </TableRow>
              ) : (
                reports
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((report) => (
                    <TableRow hover key={report.id}>
                      <TableCell>{formatDate(report.timestamp)}</TableCell>
                      <TableCell>{report.userName || 'Nieznany'}</TableCell>
                      <TableCell>{report.path || '/'}</TableCell>
                      <TableCell>
                        {report.description.length > 50
                          ? `${report.description.substring(0, 50)}...`
                          : report.description}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={statusLabels[report.status] || 'Nowy'} 
                          color={statusColors[report.status] || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Szczegóły">
                          <IconButton onClick={() => handleViewDetails(report)} size="small">
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Usuń">
                          <IconButton 
                            onClick={() => handleDeleteReport(report.id)} 
                            size="small"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={reports.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Wierszy na stronie:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} z ${count}`}
        />
      </Paper>

      {/* Dialog ze szczegółami zgłoszenia */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        {selectedReport && (
          <>
            <DialogTitle>
              Szczegóły zgłoszenia
              <Typography variant="subtitle2" color="text.secondary">
                {formatDate(selectedReport.timestamp)}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Informacje o zgłoszeniu
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Użytkownik:
                    </Typography>
                    <Typography variant="body1">
                      {selectedReport.userName || 'Nieznany'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      ID użytkownika:
                    </Typography>
                    <Typography variant="body1">
                      {selectedReport.userId || 'Brak'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Strona:
                    </Typography>
                    <Typography variant="body1">
                      {selectedReport.path || '/'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Status:
                    </Typography>
                    <Chip 
                      label={statusLabels[selectedReport.status] || 'Nowy'} 
                      color={statusColors[selectedReport.status] || 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Opis błędu
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedReport.description}
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Informacje techniczne
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="body2" color="text.secondary">
                    Przeglądarka:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }}>
                    {selectedReport.browser || 'Brak danych'}
                  </Typography>
                </Paper>
              </Box>

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Komentarz administratora
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  placeholder="Dodaj komentarz (opcjonalnie)"
                  value={comment}
                  onChange={handleCommentChange}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails} color="inherit">
                Zamknij
              </Button>
              <Button 
                onClick={() => handleStatusChange('rejected')}
                color="error"
                startIcon={<CloseIcon />}
              >
                Odrzuć
              </Button>
              <Button 
                onClick={() => handleStatusChange('inProgress')}
                color="warning"
              >
                Oznacz jako "W trakcie"
              </Button>
              <Button 
                onClick={() => handleStatusChange('resolved')}
                color="success"
                startIcon={<CheckIcon />}
              >
                Oznacz jako rozwiązany
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ErrorReports;
